import { useState, useMemo, useEffect } from "react";
import { useAuth } from "wasp/client/auth";
import { useQuery } from "wasp/client/operations";
import {
  getCanadaProvinces,
  getCanadaMunicipalities,
  getCanadaMunicipalityById,
  getCanadaNeighbourhoods,
  getCanadaNeighbourhoodById,
  getCanadaSuburbs,
  getCanadaSuburbById,
  updateCanadaProvince,
  createCanadaMunicipality,
  updateCanadaMunicipality,
  deleteCanadaMunicipality,
  seedCanadaProvinces,

  createCanadaNeighbourhood,
  updateCanadaNeighbourhood,
  deleteCanadaNeighbourhood,
  createCanadaSuburb,
  updateCanadaSuburb,
  deleteCanadaSuburb,
  addNeighbourhoodFsa,
  removeNeighbourhoodFsa,
} from "wasp/client/operations";
import { MapPin, Plus, Filter, Sprout, X } from "lucide-react";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { AdminFlyoutPane } from "./components/AdminFlyoutPane";
import { FlyoutGridLayout, FlyoutGridSection, FlyoutGridRow } from "./components/FlyoutGridLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "../hooks/use-toast";

type Tab = "provinces" | "municipalities" | "suburbs" | "neighbourhoods";

const MUNICIPALITY_TYPES = [
  "CITY", "TOWN", "VILLAGE", "MUNICIPALITY", "TOWNSHIP", "HAMLET", "DISTRICT",
] as const;

const MUNICIPALITY_TYPE_LABELS: Record<string, string> = {
  CITY: "City", TOWN: "Town", VILLAGE: "Village", MUNICIPALITY: "Municipality",
  TOWNSHIP: "Township", HAMLET: "Hamlet", DISTRICT: "District",
};

export default function AdminCanadaPage() {
  const { data: user } = useAuth();
  if (!user) return <LoadingSpinner />;

  const [activeTab, setActiveTab] = useState<Tab>("provinces");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [municipalityFilter, setMunicipalityFilter] = useState("");

  // Flyout state
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<"view" | "edit" | "create">("view");
  const [newFsaInput, setNewFsaInput] = useState("");

  // Province form
  const [provinceForm, setProvinceForm] = useState({ name: "", code: "", isActive: true });

  // Municipality form
  const [municipalityForm, setMunicipalityForm] = useState({
    name: "", provinceId: "", type: "CITY", website: "", isActive: true,
  });

  // Suburb form
  const [suburbForm, setSuburbForm] = useState({
    name: "", municipalityId: "", isActive: true,
  });

  // Neighbourhood form
  const [neighbourhoodForm, setNeighbourhoodForm] = useState({
    name: "", municipalityId: "", suburbId: "", isActive: true,
  });

  // ── Queries ─────────────────────────────────────────────────────────────────
  const { data: provinces = [], isLoading: loadingProvinces, refetch: refetchProvinces } =
    useQuery(getCanadaProvinces);

  const { data: municipalities = [], isLoading: loadingMunicipalities, refetch: refetchMunicipalities } =
    useQuery(getCanadaMunicipalities, {});

  const { data: neighbourhoods = [], isLoading: loadingNeighbourhoods, refetch: refetchNeighbourhoods } =
    useQuery(getCanadaNeighbourhoods, {});

  const { data: suburbs = [], isLoading: loadingSuburbs, refetch: refetchSuburbs } =
    useQuery(getCanadaSuburbs, {});

  const { data: selectedMunicipality } = useQuery(
    getCanadaMunicipalityById,
    { id: selectedId || "" },
    { enabled: activeTab === "municipalities" && !!selectedId && flyoutMode !== "create" }
  );

  const { data: selectedNeighbourhood, refetch: refetchSelectedNeighbourhood } = useQuery(
    getCanadaNeighbourhoodById,
    { id: selectedId || "" },
    { enabled: activeTab === "neighbourhoods" && !!selectedId && flyoutMode !== "create" }
  );

  const { data: selectedSuburb } = useQuery(
    getCanadaSuburbById,
    { id: selectedId || "" },
    { enabled: activeTab === "suburbs" && !!selectedId && flyoutMode !== "create" }
  );

  // ── Filtered lists ───────────────────────────────────────────────────────────
  const filteredProvinces = useMemo(() =>
    (provinces as any[]).filter((p: any) => {
      if (!searchTerm) return true;
      return p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             p.code.toLowerCase().includes(searchTerm.toLowerCase());
    }), [provinces, searchTerm]);

  const filteredMunicipalities = useMemo(() =>
    (municipalities as any[]).filter((m: any) => {
      if (searchTerm && !m.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !m.province?.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (typeFilter && m.type !== typeFilter) return false;
      if (provinceFilter && m.provinceId !== provinceFilter) return false;
      return true;
    }), [municipalities, searchTerm, typeFilter, provinceFilter]);

  const filteredSuburbs = useMemo(() =>
    (suburbs as any[]).filter((s: any) => {
      if (searchTerm && !s.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !s.municipality?.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (provinceFilter && s.municipality?.province?.id !== provinceFilter) return false;
      if (municipalityFilter && s.municipalityId !== municipalityFilter) return false;
      return true;
    }), [suburbs, searchTerm, provinceFilter, municipalityFilter]);

  const filteredNeighbourhoods = useMemo(() =>
    (neighbourhoods as any[]).filter((n: any) => {
      if (searchTerm && !n.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !n.municipality?.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (provinceFilter && n.municipality?.province?.id !== provinceFilter) return false;
      if (municipalityFilter && n.municipalityId !== municipalityFilter) return false;
      return true;
    }), [neighbourhoods, searchTerm, provinceFilter, municipalityFilter]);

  // ── Selected items ───────────────────────────────────────────────────────────
  const selectedProvince = useMemo(
    () => (provinces as any[]).find((p: any) => p.id === selectedId),
    [provinces, selectedId]
  );

  // Sync forms when selection changes
  useEffect(() => {
    if (selectedProvince && activeTab === "provinces") {
      setProvinceForm({ name: selectedProvince.name || "", code: selectedProvince.code || "", isActive: selectedProvince.isActive ?? true });
    }
  }, [selectedProvince, activeTab]);

  useEffect(() => {
    if (selectedMunicipality && flyoutMode !== "create") {
      const m = selectedMunicipality as any;
      setMunicipalityForm({ name: m.name || "", provinceId: m.provinceId || "", type: m.type || "CITY", website: m.website || "", isActive: m.isActive ?? true });
    }
  }, [selectedMunicipality, flyoutMode]);

  useEffect(() => {
    if (selectedNeighbourhood && flyoutMode !== "create") {
      const n = selectedNeighbourhood as any;
      setNeighbourhoodForm({ name: n.name || "", municipalityId: n.municipalityId || "", suburbId: n.suburbId || "", isActive: n.isActive ?? true });
    }
  }, [selectedNeighbourhood, flyoutMode]);

  useEffect(() => {
    if (selectedSuburb && flyoutMode !== "create") {
      const s = selectedSuburb as any;
      setSuburbForm({ name: s.name || "", municipalityId: s.municipalityId || "", isActive: s.isActive ?? true });
    }
  }, [selectedSuburb, flyoutMode]);

  // ── Navigation ───────────────────────────────────────────────────────────────
  const filteredList =
    activeTab === "provinces" ? filteredProvinces :
    activeTab === "municipalities" ? filteredMunicipalities :
    activeTab === "suburbs" ? filteredSuburbs :
    filteredNeighbourhoods;

  const currentIndex = filteredList.findIndex((item: any) => item.id === selectedId);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < filteredList.length - 1;

  const handleNavigatePrevious = () => { if (hasPrevious) { setSelectedId(filteredList[currentIndex - 1].id); setFlyoutMode("view"); } };
  const handleNavigateNext    = () => { if (hasNext)     { setSelectedId(filteredList[currentIndex + 1].id); setFlyoutMode("view"); } };

  const closeAndReset = () => { setSelectedId(null); setFlyoutMode("view"); setNewFsaInput(""); };

  const switchTab = (tab: Tab) => {
    setActiveTab(tab); setSelectedId(null); setFlyoutMode("view");
    setSearchTerm(""); setTypeFilter(""); setProvinceFilter(""); setMunicipalityFilter("");
  };

  // ── Province handlers ────────────────────────────────────────────────────────
  const handleSaveProvince = async () => {
    if (!selectedId) return;
    try {
      await (updateCanadaProvince as any)({ id: selectedId, ...provinceForm });
      await refetchProvinces();
      setFlyoutMode("view");
      toast({ title: "Province updated" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleSeedProvinces = async () => {
    try {
      const result = await (seedCanadaProvinces as any)({});
      await refetchProvinces();
      toast({ title: "Provinces seeded", description: `Created: ${result.created}, Skipped: ${result.skipped}` });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };



  // ── Municipality handlers ────────────────────────────────────────────────────
  const handleCreateMunicipality = async () => {
    try {
      await (createCanadaMunicipality as any)(municipalityForm);
      await refetchMunicipalities();
      closeAndReset();
      toast({ title: "Municipality created" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleSaveMunicipality = async () => {
    if (!selectedId) return;
    try {
      await (updateCanadaMunicipality as any)({ id: selectedId, ...municipalityForm });
      await refetchMunicipalities();
      setFlyoutMode("view");
      toast({ title: "Municipality updated" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleDeleteMunicipality = async () => {
    if (!selectedId || !confirm("Delete this municipality and all its neighbourhoods?")) return;
    try {
      await (deleteCanadaMunicipality as any)({ id: selectedId });
      await refetchMunicipalities();
      closeAndReset();
      toast({ title: "Municipality deleted" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  // ── Neighbourhood handlers ───────────────────────────────────────────────────
  const handleCreateNeighbourhood = async () => {
    try {
      await (createCanadaNeighbourhood as any)(neighbourhoodForm);
      await refetchNeighbourhoods();
      closeAndReset();
      toast({ title: "Neighbourhood created" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleSaveNeighbourhood = async () => {
    if (!selectedId) return;
    try {
      await (updateCanadaNeighbourhood as any)({ id: selectedId, ...neighbourhoodForm });
      await refetchNeighbourhoods();
      setFlyoutMode("view");
      toast({ title: "Neighbourhood updated" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleDeleteNeighbourhood = async () => {
    if (!selectedId || !confirm("Delete this neighbourhood?")) return;
    try {
      await (deleteCanadaNeighbourhood as any)({ id: selectedId });
      await refetchNeighbourhoods();
      closeAndReset();
      toast({ title: "Neighbourhood deleted" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  // ── Suburb handlers ────────────────────────────────────────────────────────
  const handleCreateSuburb = async () => {
    try {
      await (createCanadaSuburb as any)(suburbForm);
      await refetchSuburbs();
      closeAndReset();
      toast({ title: "Suburb created" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleSaveSuburb = async () => {
    if (!selectedId) return;
    try {
      await (updateCanadaSuburb as any)({ id: selectedId, ...suburbForm });
      await refetchSuburbs();
      setFlyoutMode("view");
      toast({ title: "Suburb updated" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleDeleteSuburb = async () => {
    if (!selectedId || !confirm("Delete this suburb?")) return;
    try {
      await (deleteCanadaSuburb as any)({ id: selectedId });
      await refetchSuburbs();
      closeAndReset();
      toast({ title: "Suburb deleted" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleAddFsa = async () => {
    if (!selectedId || !newFsaInput.trim()) return;
    try {
      await (addNeighbourhoodFsa as any)({ neighbourhoodId: selectedId, fsa: newFsaInput.trim().toUpperCase() });
      await refetchSelectedNeighbourhood();
      setNewFsaInput("");
      toast({ title: "FSA added" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const handleRemoveFsa = async (fsaId: string) => {
    try {
      await (removeNeighbourhoodFsa as any)({ id: fsaId });
      await refetchSelectedNeighbourhood();
      toast({ title: "FSA removed" });
    } catch (e: any) { toast({ title: "Error", description: e.message, variant: "destructive" }); }
  };

  const isLoading =
    activeTab === "provinces" ? loadingProvinces :
    activeTab === "municipalities" ? loadingMunicipalities :
    activeTab === "suburbs" ? loadingSuburbs :
    loadingNeighbourhoods;

  const flyoutOpen = !!selectedId || flyoutMode === "create";

  const flyoutTitle =
    flyoutMode === "create" ? (activeTab === "municipalities" ? "New Municipality" : activeTab === "suburbs" ? "New Suburb" : "New Neighbourhood") :
    activeTab === "provinces" ? (selectedProvince?.name ?? "Province") :
    activeTab === "municipalities" ? ((selectedMunicipality as any)?.name ?? "Municipality") :
    activeTab === "suburbs" ? ((selectedSuburb as any)?.name ?? "Suburb") :
    ((selectedNeighbourhood as any)?.name ?? "Neighbourhood");

  const flyoutDescription =
    flyoutMode === "create" ? "" :
    activeTab === "provinces" ? `${selectedProvince?.type ?? ""} · ${selectedProvince?.code ?? ""}` :
    activeTab === "municipalities" ? `${MUNICIPALITY_TYPE_LABELS[(selectedMunicipality as any)?.type] ?? ""} · ${(selectedMunicipality as any)?.province?.name ?? ""}` :
    activeTab === "suburbs" ? `${(selectedSuburb as any)?.municipality?.name ?? ""} · ${(selectedSuburb as any)?.municipality?.province?.name ?? ""}` :
    `${(selectedNeighbourhood as any)?.municipality?.name ?? ""} · ${(selectedNeighbourhood as any)?.municipality?.province?.name ?? ""}`;

  const tabCounts = {
    provinces: (provinces as any[]).length,
    municipalities: (municipalities as any[]).length,
    suburbs: (suburbs as any[]).length,
    neighbourhoods: (neighbourhoods as any[]).length,
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-4 border-b bg-white shrink-0 gap-4">
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-red-600" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Canada</h1>
            <p className="text-xs text-gray-500">Provinces, municipalities, and neighbourhoods</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeTab === "provinces" && (
            <>
              <Button variant="outline" size="sm" onClick={handleSeedProvinces} className="gap-1 text-[10px] sm:text-xs px-2 py-1 h-auto">
                <Sprout className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                Seed Prov
              </Button>

            </>
          )}
          {(activeTab === "municipalities" || activeTab === "suburbs" || activeTab === "neighbourhoods") && (
            <EnhancedActionButton
              icon={Plus}
              label="Add"
              className="text-xs px-2 py-1 h-auto"
              onClick={() => {
                setSelectedId(null);
                if (activeTab === "municipalities") setMunicipalityForm({ name: "", provinceId: "", type: "CITY", website: "", isActive: true });
                else if (activeTab === "suburbs") setSuburbForm({ name: "", municipalityId: "", isActive: true });
                else setNeighbourhoodForm({ name: "", municipalityId: "", suburbId: "", isActive: true });
                setFlyoutMode("create");
              }}
            />
          )}
          <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} className="text-xs px-2 py-1 h-auto">
            <Filter className="w-3.5 h-3.5 mr-1" />
            Filters
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-white px-6 shrink-0">
        {(["provinces", "municipalities", "suburbs", "neighbourhoods"] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => switchTab(tab)}
            className={`px-4 py-3 text-sm font-medium capitalize border-b-2 transition-colors ${
              activeTab === tab ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab === "provinces" ? "Provinces & Territories" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
              {tabCounts[tab]}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-6 py-3 bg-gray-50 border-b shrink-0 flex flex-wrap gap-3 items-center">
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-48 h-8 text-sm"
          />
          {activeTab === "municipalities" && (
            <>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border rounded px-2 h-8 text-sm bg-white">
                <option value="">All types</option>
                {MUNICIPALITY_TYPES.map((t) => <option key={t} value={t}>{MUNICIPALITY_TYPE_LABELS[t]}</option>)}
              </select>
              <select value={provinceFilter} onChange={(e) => setProvinceFilter(e.target.value)} className="border rounded px-2 h-8 text-sm bg-white">
                <option value="">All provinces</option>
                {(provinces as any[]).map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </>
          )}
          {activeTab === "neighbourhoods" && (
            <>
              <select value={provinceFilter} onChange={(e) => { setProvinceFilter(e.target.value); setMunicipalityFilter(""); }} className="border rounded px-2 h-8 text-sm bg-white">
                <option value="">All provinces</option>
                {(provinces as any[]).map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={municipalityFilter} onChange={(e) => setMunicipalityFilter(e.target.value)} className="border rounded px-2 h-8 text-sm bg-white">
                <option value="">All municipalities</option>
                {(municipalities as any[])
                  .filter((m: any) => !provinceFilter || m.provinceId === provinceFilter)
                  .map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </>
          )}
          {activeTab === "suburbs" && (
            <>
              <select value={provinceFilter} onChange={(e) => { setProvinceFilter(e.target.value); setMunicipalityFilter(""); }} className="border rounded px-2 h-8 text-sm bg-white">
                <option value="">All provinces</option>
                {(provinces as any[]).map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={municipalityFilter} onChange={(e) => setMunicipalityFilter(e.target.value)} className="border rounded px-2 h-8 text-sm bg-white">
                <option value="">All municipalities</option>
                {(municipalities as any[])
                  .filter((m: any) => !provinceFilter || m.provinceId === provinceFilter)
                  .map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </>
          )}
          {(searchTerm || typeFilter || provinceFilter || municipalityFilter) && (
            <Button variant="ghost" size="sm" onClick={() => { setSearchTerm(""); setTypeFilter(""); setProvinceFilter(""); setMunicipalityFilter(""); }} className="text-xs">
              Clear
            </Button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <LoadingSpinner />
          ) : activeTab === "provinces" ? (
            <ProvinceTable rows={filteredProvinces} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); setFlyoutMode("view"); }} />
          ) : activeTab === "municipalities" ? (
            <MunicipalityTable rows={filteredMunicipalities} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); setFlyoutMode("view"); }} />
          ) : activeTab === "suburbs" ? (
            <SuburbTable rows={filteredSuburbs} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); setFlyoutMode("view"); }} />
          ) : (
            <NeighbourhoodTable rows={filteredNeighbourhoods} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); setFlyoutMode("view"); }} />
          )}
        </div>

        {/* Flyout */}
        <AdminFlyoutPane
          open={flyoutOpen}
          onOpenChange={(open) => { if (!open) closeAndReset(); }}
          title={flyoutTitle}
          description={flyoutDescription}
          width="xl"
          mode={flyoutMode === "create" ? "edit" : flyoutMode}
          headerActions={
            flyoutMode === "view" ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setFlyoutMode("edit")}>Edit</Button>
                {activeTab !== "provinces" && (
                  <Button size="sm" variant="destructive"
                    onClick={activeTab === "municipalities" ? handleDeleteMunicipality : activeTab === "suburbs" ? handleDeleteSuburb : handleDeleteNeighbourhood}>
                    Delete
                  </Button>
                )}
              </div>
            ) : flyoutMode === "edit" ? (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setFlyoutMode("view")}>Cancel</Button>
                <Button size="sm" onClick={
                  activeTab === "provinces" ? handleSaveProvince :
                  activeTab === "municipalities" ? handleSaveMunicipality :
                  activeTab === "suburbs" ? handleSaveSuburb :
                  handleSaveNeighbourhood
                }>Save</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={closeAndReset}>Cancel</Button>
                <Button size="sm" onClick={
                  activeTab === "municipalities" ? handleCreateMunicipality :
                  activeTab === "suburbs" ? handleCreateSuburb :
                  handleCreateNeighbourhood
                }>Create</Button>
              </div>
            )
          }
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        >
          {/* Province flyout */}
          {activeTab === "provinces" && selectedProvince && flyoutMode !== "create" && (
            <FlyoutGridLayout>
              <FlyoutGridSection title="Details">
                <FlyoutGridRow label="Name">
                  {flyoutMode === "edit"
                    ? <Input value={provinceForm.name} onChange={(e) => setProvinceForm((f) => ({ ...f, name: e.target.value }))} className="h-8 text-sm" />
                    : <span className="text-sm">{selectedProvince.name}</span>}
                </FlyoutGridRow>
                <FlyoutGridRow label="Code">
                  {flyoutMode === "edit"
                    ? <Input value={provinceForm.code} onChange={(e) => setProvinceForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} maxLength={2} className="h-8 text-sm w-20" />
                    : <span className="text-sm font-mono">{selectedProvince.code}</span>}
                </FlyoutGridRow>
                <FlyoutGridRow label="Type">
                  <span className="text-sm capitalize">{selectedProvince.type?.toLowerCase()}</span>
                </FlyoutGridRow>
                <FlyoutGridRow label="Active">
                  {flyoutMode === "edit"
                    ? <Switch checked={provinceForm.isActive} onCheckedChange={(v) => setProvinceForm((f) => ({ ...f, isActive: v }))} />
                    : <StatusBadge active={selectedProvince.isActive} />}
                </FlyoutGridRow>
              </FlyoutGridSection>
              <FlyoutGridSection title="Statistics">
                <FlyoutGridRow label="Municipalities">
                  <span className="text-sm">{selectedProvince._count?.municipalities ?? 0}</span>
                </FlyoutGridRow>
              </FlyoutGridSection>
            </FlyoutGridLayout>
          )}

          {/* Municipality flyout */}
          {activeTab === "municipalities" && flyoutMode !== "create" && selectedMunicipality && (
            <MunicipalityFlyoutContent mode={flyoutMode} form={municipalityForm} setForm={setMunicipalityForm} provinces={provinces as any[]} />
          )}
          {activeTab === "municipalities" && flyoutMode === "create" && (
            <MunicipalityFlyoutContent mode="edit" form={municipalityForm} setForm={setMunicipalityForm} provinces={provinces as any[]} />
          )}

          {/* Suburb flyout */}
          {activeTab === "suburbs" && flyoutMode !== "create" && selectedSuburb && (
            <SuburbFlyoutContent mode={flyoutMode} form={suburbForm} setForm={setSuburbForm} municipalities={municipalities as any[]} provinces={provinces as any[]} />
          )}
          {activeTab === "suburbs" && flyoutMode === "create" && (
            <SuburbFlyoutContent mode="edit" form={suburbForm} setForm={setSuburbForm} municipalities={municipalities as any[]} provinces={provinces as any[]} />
          )}

          {/* Neighbourhood flyout */}
          {activeTab === "neighbourhoods" && flyoutMode !== "create" && selectedNeighbourhood && (
            <NeighbourhoodFlyoutContent
              mode={flyoutMode}
              form={neighbourhoodForm}
              setForm={setNeighbourhoodForm}
              municipalities={municipalities as any[]}
              suburbs={suburbs as any[]}
              provinces={provinces as any[]}
              fsaCodes={(selectedNeighbourhood as any)?.fsaCodes ?? []}
              newFsaInput={newFsaInput}
              setNewFsaInput={setNewFsaInput}
              onAddFsa={handleAddFsa}
              onRemoveFsa={handleRemoveFsa}
            />
          )}
          {activeTab === "neighbourhoods" && flyoutMode === "create" && (
            <NeighbourhoodFlyoutContent
              mode="edit"
              form={neighbourhoodForm}
              setForm={setNeighbourhoodForm}
              municipalities={municipalities as any[]}
              suburbs={suburbs as any[]}
              provinces={provinces as any[]}
              fsaCodes={[]}
              newFsaInput={newFsaInput}
              setNewFsaInput={setNewFsaInput}
              onAddFsa={handleAddFsa}
              onRemoveFsa={handleRemoveFsa}
            />
          )}
        </AdminFlyoutPane>
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// ── Province table ────────────────────────────────────────────────────────────
function ProvinceTable({ rows, selectedId, onSelect }: { rows: any[]; selectedId: string | null; onSelect: (id: string) => void }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b sticky top-0">
        <tr>
          <th className="px-4 py-2 text-left font-medium text-gray-600 w-16">Code</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Name</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Type</th>
          <th className="px-4 py-2 text-right font-medium text-gray-600 w-28">Municipalities</th>
          <th className="px-4 py-2 text-center font-medium text-gray-600 w-20">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {rows.length === 0 && (
          <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No provinces found. Use "Seed Provinces" to populate.</td></tr>
        )}
        {rows.map((p: any) => (
          <tr key={p.id} onClick={() => onSelect(p.id)} className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedId === p.id ? "bg-blue-50" : ""}`}>
            <td className="px-4 py-3 font-mono font-semibold text-gray-700">{p.code}</td>
            <td className="px-4 py-3 font-medium text-gray-900">{p.name}</td>
            <td className="px-4 py-3 text-gray-500 capitalize">{p.type?.toLowerCase()}</td>
            <td className="px-4 py-3 text-right text-gray-600">{p._count?.municipalities ?? 0}</td>
            <td className="px-4 py-3 text-center"><StatusBadge active={p.isActive} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Municipality table ────────────────────────────────────────────────────────
function MunicipalityTable({ rows, selectedId, onSelect }: { rows: any[]; selectedId: string | null; onSelect: (id: string) => void }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b sticky top-0">
        <tr>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Name</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Province</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Type</th>
          <th className="px-4 py-2 text-right font-medium text-gray-600 w-28">Neighbourhoods</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Website</th>
          <th className="px-4 py-2 text-center font-medium text-gray-600 w-20">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {rows.length === 0 && (
          <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No municipalities found.</td></tr>
        )}
        {rows.map((m: any) => (
          <tr key={m.id} onClick={() => onSelect(m.id)} className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedId === m.id ? "bg-blue-50" : ""}`}>
            <td className="px-4 py-3 font-medium text-gray-900">{m.name}</td>
            <td className="px-4 py-3 text-gray-600">
              <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded mr-1">{m.province?.code}</span>
              {m.province?.name}
            </td>
            <td className="px-4 py-3 text-gray-500">{MUNICIPALITY_TYPE_LABELS[m.type] ?? m.type}</td>
            <td className="px-4 py-3 text-right text-gray-600">{m._count?.neighbourhoods ?? 0}</td>
            <td className="px-4 py-3 text-gray-500 truncate max-w-[140px]">
              {m.website
                ? <a href={m.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:underline text-xs">{m.website.replace(/^https?:\/\//, "")}</a>
                : "—"}
            </td>
            <td className="px-4 py-3 text-center"><StatusBadge active={m.isActive} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Suburb table ──────────────────────────────────────────────────────────────
function SuburbTable({ rows, selectedId, onSelect }: { rows: any[]; selectedId: string | null; onSelect: (id: string) => void }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b sticky top-0">
        <tr>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Suburb</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Municipality</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Province</th>
          <th className="px-4 py-2 text-right font-medium text-gray-600 w-28">Neighbourhoods</th>
          <th className="px-4 py-2 text-center font-medium text-gray-600 w-20">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {rows.length === 0 && (
          <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No suburbs found.</td></tr>
        )}
        {rows.map((s: any) => (
          <tr key={s.id} onClick={() => onSelect(s.id)} className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedId === s.id ? "bg-blue-50" : ""}`}>
            <td className="px-4 py-3 font-medium text-gray-900">{s.name}</td>
            <td className="px-4 py-3 text-gray-600">{s.municipality?.name}</td>
            <td className="px-4 py-3 text-gray-500">
              <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded mr-1">{s.municipality?.province?.code}</span>
              {s.municipality?.province?.name}
            </td>
            <td className="px-4 py-3 text-right text-gray-600">{s._count?.neighbourhoods ?? 0}</td>
            <td className="px-4 py-3 text-center"><StatusBadge active={s.isActive} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Neighbourhood table ───────────────────────────────────────────────────────
function NeighbourhoodTable({ rows, selectedId, onSelect }: { rows: any[]; selectedId: string | null; onSelect: (id: string) => void }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b sticky top-0">
        <tr>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Neighbourhood</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Municipality</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Suburb</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">Province</th>
          <th className="px-4 py-2 text-left font-medium text-gray-600">FSA Codes</th>
          <th className="px-4 py-2 text-center font-medium text-gray-600 w-20">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {rows.length === 0 && (
          <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No neighbourhoods found.</td></tr>
        )}
        {rows.map((n: any) => (
          <tr key={n.id} onClick={() => onSelect(n.id)} className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedId === n.id ? "bg-blue-50" : ""}`}>
            <td className="px-4 py-3 font-medium text-gray-900">{n.name}</td>
            <td className="px-4 py-3 text-gray-600">{n.municipality?.name}</td>
            <td className="px-4 py-3 text-gray-500">{n.suburb?.name ?? <span className="text-gray-300">—</span>}</td>
            <td className="px-4 py-3 text-gray-500">
              <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded mr-1">{n.municipality?.province?.code}</span>
              {n.municipality?.province?.name}
            </td>
            <td className="px-4 py-3">
              <div className="flex flex-wrap gap-1">
                {(n.fsaCodes ?? []).map((f: any) => (
                  <span key={f.id} className="font-mono text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{f.fsa}</span>
                ))}
                {(!n.fsaCodes || n.fsaCodes.length === 0) && <span className="text-gray-400">—</span>}
              </div>
            </td>
            <td className="px-4 py-3 text-center"><StatusBadge active={n.isActive} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Municipality flyout form ──────────────────────────────────────────────────
function MunicipalityFlyoutContent({ mode, form, setForm, provinces }: {
  mode: "view" | "edit"; form: any; setForm: (u: (f: any) => any) => void; provinces: any[];
}) {
  return (
    <FlyoutGridLayout>
      <FlyoutGridSection title="Basic Information">
        <FlyoutGridRow label="Name">
          {mode === "edit"
            ? <Input value={form.name} onChange={(e) => setForm((f: any) => ({ ...f, name: e.target.value }))} className="h-8 text-sm" />
            : <span className="text-sm">{form.name}</span>}
        </FlyoutGridRow>
        <FlyoutGridRow label="Province">
          {mode === "edit"
            ? <select value={form.provinceId} onChange={(e) => setForm((f: any) => ({ ...f, provinceId: e.target.value }))} className="border rounded px-2 h-8 text-sm w-full bg-white">
                <option value="">Select province…</option>
                {provinces.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            : <span className="text-sm">{provinces.find((p: any) => p.id === form.provinceId)?.name ?? "—"}</span>}
        </FlyoutGridRow>
        <FlyoutGridRow label="Type">
          {mode === "edit"
            ? <select value={form.type} onChange={(e) => setForm((f: any) => ({ ...f, type: e.target.value }))} className="border rounded px-2 h-8 text-sm bg-white">
                {MUNICIPALITY_TYPES.map((t) => <option key={t} value={t}>{MUNICIPALITY_TYPE_LABELS[t]}</option>)}
              </select>
            : <span className="text-sm">{MUNICIPALITY_TYPE_LABELS[form.type] ?? form.type}</span>}
        </FlyoutGridRow>
      </FlyoutGridSection>
      <FlyoutGridSection title="Details">
        <FlyoutGridRow label="Website">
          {mode === "edit"
            ? <Input value={form.website} onChange={(e) => setForm((f: any) => ({ ...f, website: e.target.value }))} placeholder="https://..." className="h-8 text-sm" />
            : form.website
              ? <a href={form.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">{form.website}</a>
              : <span className="text-sm text-gray-400">—</span>}
        </FlyoutGridRow>
        <FlyoutGridRow label="Active">
          {mode === "edit"
            ? <Switch checked={form.isActive} onCheckedChange={(v) => setForm((f: any) => ({ ...f, isActive: v }))} />
            : <StatusBadge active={form.isActive} />}
        </FlyoutGridRow>
      </FlyoutGridSection>
    </FlyoutGridLayout>
  );
}

// ── Suburb flyout form ────────────────────────────────────────────────────────
function SuburbFlyoutContent({ mode, form, setForm, municipalities, provinces }: {
  mode: "view" | "edit"; form: any; setForm: (u: (f: any) => any) => void; municipalities: any[]; provinces: any[];
}) {
  const [provinceFilter, setProvinceFilter] = useState("");
  const filteredMunis = municipalities.filter((m: any) => !provinceFilter || m.provinceId === provinceFilter);

  return (
    <FlyoutGridLayout>
      <FlyoutGridSection title="Basic Information">
        <FlyoutGridRow label="Name">
          {mode === "edit"
            ? <Input value={form.name} onChange={(e) => setForm((f: any) => ({ ...f, name: e.target.value }))} className="h-8 text-sm" />
            : <span className="text-sm">{form.name}</span>}
        </FlyoutGridRow>
        {mode === "edit" && (
          <FlyoutGridRow label="Province">
            <select value={provinceFilter} onChange={(e) => { setProvinceFilter(e.target.value); setForm((f: any) => ({ ...f, municipalityId: "" })); }} className="border rounded px-2 h-8 text-sm w-full bg-white">
              <option value="">Filter by province…</option>
              {provinces.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FlyoutGridRow>
        )}
        <FlyoutGridRow label="Municipality">
          {mode === "edit"
            ? <select value={form.municipalityId} onChange={(e) => setForm((f: any) => ({ ...f, municipalityId: e.target.value }))} className="border rounded px-2 h-8 text-sm w-full bg-white">
                <option value="">Select municipality…</option>
                {filteredMunis.map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            : <span className="text-sm">{municipalities.find((m: any) => m.id === form.municipalityId)?.name ?? "—"}</span>}
        </FlyoutGridRow>
        <FlyoutGridRow label="Active">
          {mode === "edit"
            ? <Switch checked={form.isActive} onCheckedChange={(v) => setForm((f: any) => ({ ...f, isActive: v }))} />
            : <StatusBadge active={form.isActive} />}
        </FlyoutGridRow>
      </FlyoutGridSection>
    </FlyoutGridLayout>
  );
}

// ── Neighbourhood flyout form ─────────────────────────────────────────────────
function NeighbourhoodFlyoutContent({ mode, form, setForm, municipalities, suburbs, provinces, fsaCodes, newFsaInput, setNewFsaInput, onAddFsa, onRemoveFsa }: {
  mode: "view" | "edit"; form: any; setForm: (u: (f: any) => any) => void;
  municipalities: any[]; suburbs: any[]; provinces: any[];
  fsaCodes: any[]; newFsaInput: string; setNewFsaInput: (v: string) => void;
  onAddFsa: () => void; onRemoveFsa: (id: string) => void;
}) {
  const [provinceFilter, setProvinceFilter] = useState("");
  const filteredMunis = municipalities.filter((m: any) => !provinceFilter || m.provinceId === provinceFilter);

  return (
    <FlyoutGridLayout>
      <FlyoutGridSection title="Basic Information">
        <FlyoutGridRow label="Name">
          {mode === "edit"
            ? <Input value={form.name} onChange={(e) => setForm((f: any) => ({ ...f, name: e.target.value }))} className="h-8 text-sm" />
            : <span className="text-sm">{form.name}</span>}
        </FlyoutGridRow>
        {mode === "edit" && (
          <FlyoutGridRow label="Province">
            <select value={provinceFilter} onChange={(e) => { setProvinceFilter(e.target.value); setForm((f: any) => ({ ...f, municipalityId: "" })); }} className="border rounded px-2 h-8 text-sm w-full bg-white">
              <option value="">Filter by province…</option>
              {provinces.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FlyoutGridRow>
        )}
        <FlyoutGridRow label="Municipality">
          {mode === "edit"
            ? <select value={form.municipalityId} onChange={(e) => setForm((f: any) => ({ ...f, municipalityId: e.target.value, suburbId: "" }))} className="border rounded px-2 h-8 text-sm w-full bg-white">
                <option value="">Select municipality…</option>
                {filteredMunis.map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            : <span className="text-sm">{municipalities.find((m: any) => m.id === form.municipalityId)?.name ?? "—"}</span>}
        </FlyoutGridRow>
        <FlyoutGridRow label="Suburb (Optional)">
          {mode === "edit"
            ? <select value={form.suburbId} onChange={(e) => setForm((f: any) => ({ ...f, suburbId: e.target.value }))} className="border rounded px-2 h-8 text-sm w-full bg-white">
                <option value="">Select suburb…</option>
                {suburbs.filter((s: any) => s.municipalityId === form.municipalityId).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            : <span className="text-sm">{suburbs.find((s: any) => s.id === form.suburbId)?.name ?? "—"}</span>}
        </FlyoutGridRow>
        <FlyoutGridRow label="Active">
          {mode === "edit"
            ? <Switch checked={form.isActive} onCheckedChange={(v) => setForm((f: any) => ({ ...f, isActive: v }))} />
            : <StatusBadge active={form.isActive} />}
        </FlyoutGridRow>
      </FlyoutGridSection>

      <FlyoutGridSection title="FSA Codes">
        <div className="px-2 py-1 space-y-2">
          <div className="flex flex-wrap gap-2">
            {fsaCodes.map((f: any) => (
              <span key={f.id} className="inline-flex items-center gap-1 font-mono text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                {f.fsa}
                <button onClick={() => onRemoveFsa(f.id)} className="hover:text-red-600 transition-colors" title="Remove FSA">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {fsaCodes.length === 0 && <span className="text-xs text-gray-400">No FSA codes yet</span>}
          </div>
          <div className="flex gap-2 items-center pt-1">
            <Input
              value={newFsaInput}
              onChange={(e) => setNewFsaInput(e.target.value.toUpperCase().slice(0, 3))}
              placeholder="e.g. M5V"
              maxLength={3}
              className="h-7 text-sm w-24 font-mono"
              onKeyDown={(e) => { if (e.key === "Enter") onAddFsa(); }}
            />
            <Button size="sm" variant="outline" onClick={onAddFsa} disabled={newFsaInput.length !== 3} className="h-7 text-xs px-2">
              Add FSA
            </Button>
          </div>
          <p className="text-xs text-gray-400">First 3 characters of a Canadian postal code (e.g. M5V, T2R)</p>
        </div>
      </FlyoutGridSection>
    </FlyoutGridLayout>
  );
}



